/**
 * <AuthBootstrap> — runs the cold-start auth sequence before rendering
 * the routed app.
 *
 * Behavior:
 *   1. On mount, calls bootstrapAuth() exactly once.
 *   2. While that promise is in flight, renders a full-screen logo
 *      pre-loader (matches main.tsx Suspense fallback for visual continuity).
 *   3. When the promise settles — regardless of outcome — renders
 *      children. The routing layer (PrivateRoute) is responsible for
 *      redirecting unauthenticated users to /login.
 *
 * StrictMode double-invokes effects in dev. The bootstrap is idempotent:
 *   - getAccessToken dedupes concurrent refresh calls
 *   - tokenMemory.set is idempotent
 *   - applyUserToStore writes the same data on repeat
 * so a double-invoke is harmless beyond a single redundant network round-trip.
 *
 * See:
 *   - docs/adr/0009-storage-architecture-and-token-strategy.md
 *   - docs/api-contracts/auth.md (Bootstrap Sequence)
 */

import { useEffect, useState, type ReactNode } from 'react'
import { bootstrapAuth } from '@/shared/api/auth'
import { LogoPreLoader } from '@/shared/ui/icons'

interface AuthBootstrapProps {
  children: ReactNode
}

export function AuthBootstrap({ children }: AuthBootstrapProps) {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    bootstrapAuth().finally(() => {
      if (!cancelled) setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!ready) {
    return (
      <div className='flex items-center justify-center w-full h-screen'>
        <LogoPreLoader size='large' />
      </div>
    )
  }

  return <>{children}</>
}
